import {
  memo,
  useContext,
  useEffect,
  type MutableRefObject,
  type ReactNode,
  useRef,
  useDeferredValue,
  forwardRef,
  useImperativeHandle,
  type RefObject,
} from 'react';

import { CanvasContext } from './CanvasContext.js';
import { CanvasShape } from './types/canvas-shape.js';

const setupCanvas = (
  canvasElement: HTMLCanvasElement,
  width: number,
  height: number,
): CanvasRenderingContext2D | null => {
  //* Get the device pixel ratio, falling back to 1. */
  const dpr = Math.max(1, window.devicePixelRatio) || 1;

  /**
   * Give the canvasElement pixel dimensions of
   * their CSS size * the device pixel ratio.
   */
  canvasElement.width = width * dpr;
  canvasElement.height = height * dpr;

  const context = canvasElement.getContext('2d');

  /**
   * Scale all drawing operations by the dpr, so you
   * don't have to worry about the difference.
   */
  context?.scale(dpr, dpr);

  /** Set the "drawn" size of the canvas */
  canvasElement.style.width = '100%';
  canvasElement.style.height = '100%';

  return context;
};

/** Draw the shape */
const draw = (
  canvasElement: HTMLCanvasElement | null,
  context: CanvasRenderingContext2D,
  shapeRefs: MutableRefObject<CanvasShape>[],
) => {
  if (!canvasElement) {
    return;
  }

  if (context) {
    /** Clear the last draw */
    context.clearRect(0, 0, canvasElement.width, canvasElement.height);

    shapeRefs.forEach((ref) => {
      ref.current.draw(context, ref.current.id);
    });
  }
};

type CurrentContext = {
  ctx: CanvasRenderingContext2D | null;
  prevHeight: number;
  prevWidth: number;
};

function ensureCanvasContext(
  currentCtx: CurrentContext,
  canvasRef: RefObject<HTMLCanvasElement>,
  width: number,
  height: number,
) {
  if (
    !currentCtx.ctx ||
    currentCtx.prevHeight !== height ||
    currentCtx.prevWidth !== width
  ) {
    if (canvasRef.current) {
      currentCtx.ctx = setupCanvas(canvasRef.current, width, height);
    } else {
      currentCtx.ctx = null;
    }
    currentCtx.prevHeight = height;
    currentCtx.prevWidth = width;
  }
}

type CanvasProps = {
  children?: ReactNode;
  width: number;
  height: number;
};

const MIN_WIDTH_CANVAS = 15;

export type CanvasRef = Partial<HTMLCanvasElement> & {
  customSetStyle: (key: string, value: string) => void;
};

export const Canvas = memo(
  forwardRef<CanvasRef, CanvasProps>(
    ({ children, ...props }: CanvasProps, ref) => {
      const { height, width } = props;
      const canvasRef = useRef<HTMLCanvasElement>(null);
      const { shapeRefs } = useContext(CanvasContext);
      const contextRef = useRef<CurrentContext>({
        ctx: null,
        prevHeight: 0,
        prevWidth: 0,
      });

      useImperativeHandle(
        ref,
        () => {
          return {
            customSetStyle: (key: string, value: string) => {
              canvasRef.current?.style.setProperty(key, value);
            },
          };
        },
        [],
      );

      /**
       * We use a deferred value to avoid unnecessary renders
       * when the width and height are not yet available.
       * This is useful for perceived performance.
       * We will add a minimum size stated on MIN_WIDTH_CANVAS,
       * where we consider some left overs. In some charts width is never exactly 0; that means when a width is very low
       * (e.g. < 15px) it effectively corresponds to “not ready” (the chart layout
       * is still loading or not fully plotted).
       *
       * Extra clarification:
       * - MIN_WIDTH_CANVAS is a guard against transient layout states and
       *   intermediate measurements while parents are still sizing.
       * - Very small widths typically come from initial mount, collapsed containers,
       *   or placeholder phases; skipping work avoids unnecessary canvas setup/draw.
       * - Once width > MIN_WIDTH_CANVAS, we consider the layout stable enough
       *   to initialize and render shapes.
       */
      const isReady = useDeferredValue(width > MIN_WIDTH_CANVAS);

      /**
       * Interacting with our canvas produces “side effects”
       * which isn’t allowed from within the render phase of a React component.
       * In order to avoid extra effects, it won't execute if canvas have no width && height
       * It must be setup using a useEffect hook.
       */
      useEffect(() => {
        if (!isReady || !canvasRef) {
          return;
        }

        ensureCanvasContext(contextRef.current, canvasRef, width, height);

        if (contextRef.current.ctx) {
          const shapes = [...shapeRefs.values()];
          requestAnimationFrame(() => {
            if (contextRef.current.ctx) {
              draw(canvasRef.current, contextRef.current.ctx, shapes);
            }
          });
        }

        // To make it more reactive, we won't clear the previously triggered draw.
        // FYI: https://bitbucket.lab.dynatrace.org/projects/APPFW/repos/design-system/pull-requests/5428/overview
        // return () => {
        //   drawId && cancelAnimationFrame(drawId);
        // };
      });

      return <canvas ref={canvasRef}>{children}</canvas>;
    },
  ),
);
